import React from 'react';
import {
  Box,
  Button,
  Center,
  Container,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useLiteAuth } from '../hooks/use-lite-auth';
import { selectedStoreAtom } from '../recoil/atoms';
import ProductsTab from '../components/ProductsTab';
import DailyCountTab from '../components/DailyCountTab';

const LitePage: React.FC = () => {
  const { isValid, errorMessage } = useLiteAuth();
  const navigate = useNavigate();
  const store = useRecoilValue(selectedStoreAtom);

  const headerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Loading
  if (isValid === null) {
    return (
      <Center minH="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.500" thickness="4px" />
          <Text color="gray.500">Verifying access…</Text>
        </VStack>
      </Center>
    );
  }

  // Invalid — explain why and redirect only once the modal is dismissed
  if (!isValid) {
    console.warn('Lite auth failed:', errorMessage);
    const goBack = () => navigate('/', { replace: true });
    return (
      <Box minH="100vh" bg="gray.50">
        <Modal
          isOpen
          onClose={goBack}
          isCentered
          closeOnOverlayClick
        >
          <ModalOverlay />
          <ModalContent mx={4}>
            <ModalHeader>Products page unavailable</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                {errorMessage ||
                  'The Products page could not be opened. Please try again.'}
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="teal" onClick={goBack}>
                OK
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  // Valid — render the lite interface
  return (
    <Box minH="100vh" bg="gray.50" display="flex" flexDirection="column">
      {/* Minimal Header */}
      <Box
        bg={headerBg}
        borderBottom="1px solid"
        borderColor={borderColor}
        px={4}
        py={3}
        flexShrink={0}
      >
        <Container maxW="container.xl" p={0}>
          <Heading size="sm" color="gray.700">
            {store?.name || 'Store'}
          </Heading>
        </Container>
      </Box>

      {/* Tabbed Interface */}
      <Box flex={1} minH={0} overflow="hidden">
        <Container maxW="container.xl" p={0} height="100%">
          <Tabs isFitted variant="line" colorScheme="teal" defaultIndex={0} height="100%" display="flex" flexDirection="column">
            <TabList bg={headerBg} px={2} flexShrink={0}>
              <Tab
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="semibold"
                py={3}
              >
                Products
              </Tab>
              <Tab
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="semibold"
                py={3}
              >
                Daily Count
              </Tab>
            </TabList>

            <TabPanels flex={1} minH={0}>
              <TabPanel px={{ base: 2, md: 4 }} py={4} height="100%">
                <ProductsTab />
              </TabPanel>
              <TabPanel px={0} py={0} height="100%" overflow="auto">
                <DailyCountTab />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Container>
      </Box>

      {/* Footer */}
      <Center
        py={3}
        borderTop="1px solid"
        borderColor={borderColor}
        flexShrink={0}
      >
        <Text fontSize="xs" color="gray.400">
          Powered by Shop &amp; Smile
        </Text>
      </Center>
    </Box>
  );
};

export default LitePage;
